import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Check, ChevronDown, SearchCheck } from 'lucide-react';

import { Avatar } from '@/components/ui/avatar';
import FormField from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import Text from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { FormSpacing } from '@/theme/size-tokens';
import { getIconPixels } from '@/theme/size-tokens';
import type { LogoAsset } from '@/types/shared/logo';

export type PreviewSelectOption<T = unknown> = {
  value: string;
  label: string;
  description?: string;
  logo?: LogoAsset;
  logoAlt?: string;
  data?: T;
};

export type PreviewSelectProps<T = unknown> = {
  /** Label text displayed above the component */
  label?: string;
  /** Helper text displayed below component */
  helperText?: string;
  /** Optional action rendered beside helper text */
  helperAction?: ReactNode;
  /** Spacing between label, control, and helper text */
  spacing?: FormSpacing;
  /** Error message to display */
  error?: string;
  /** Shows required asterisk next to label */
  required?: boolean;
  /** Disables interaction */
  disabled?: boolean;
  /** HTML id for accessibility */
  id?: string;
  /** Controlled selected value */
  value?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** List options displayed in the left pane */
  options: PreviewSelectOption<T>[];
  /** Whether to show an explicit empty option */
  allowEmpty?: boolean;
  /** Label for empty option */
  emptyOptionLabel?: string;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Empty-state text when search has no matches */
  noneFoundText?: string;
  /** Custom preview renderer for the right pane */
  renderPreview: (
    selectedOption: PreviewSelectOption<T> | null,
    context?: { surface: 'pane' | 'modal' }
  ) => ReactNode;
  /** Tailwind height class for the scrollable container (default: h-72) */
  containerHeight?: string;
  /** Multi-select mode: allows selecting multiple options (pane mode only) */
  multiSelect?: boolean;
  /** Selected values for multi-select mode */
  values?: string[];
  /** Change handler for multi-select mode */
  onMultiChange?: (values: string[]) => void;
  /** Display mode for selection UI */
  mode?: 'pane' | 'dropdown';
  /** Placeholder shown in dropdown trigger when no value is selected */
  triggerPlaceholder?: string;
  /** Optional modal title when inspecting option details in dropdown mode */
  detailsModalTitle?: string;
  /** Label for the secondary back/close action in the details modal */
  detailsModalBackLabel?: string;
  /** Label for the primary action in the details modal */
  detailsModalSelectLabel?: string;
};

/**
 * PreviewSelect - Searchable two-pane selector with inline preview.
 *
 * The left pane contains searchable options, and the right pane renders
 * arbitrary preview content for the currently selected option.
 */
function PreviewSelect<T = unknown>({
  label,
  helperText,
  helperAction,
  spacing,
  error,
  required,
  disabled = false,
  id,
  value = '',
  onChange,
  options,
  allowEmpty = true,
  emptyOptionLabel = 'None',
  searchPlaceholder = 'Search by name',
  noneFoundText = 'No options found.',
  renderPreview,
  containerHeight = 'h-86',
  multiSelect = false,
  values,
  onMultiChange,
  mode = 'pane',
  triggerPlaceholder = 'Select an option',
  detailsModalTitle = 'Option details',
  detailsModalBackLabel = 'Back',
  detailsModalSelectLabel = 'Select item',
}: Readonly<PreviewSelectProps<T>>) {
  const listboxId = useId();
  const [search, setSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeAction, setActiveAction] = useState<'select' | 'preview'>(
    'select'
  );
  const [infoOption, setInfoOption] = useState<PreviewSelectOption<T> | null>(
    null
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const suppressOutsideCloseUntilRef = useRef(0);
  const typeaheadBufferRef = useRef('');
  const typeaheadTimeoutRef = useRef<number | null>(null);

  const rowBaseClass =
    'w-full border-b border-border px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset';
  const cursorClass = disabled ? 'cursor-not-allowed' : 'cursor-pointer';

  const getRowStateClass = (isSelected: boolean) =>
    isSelected ? 'bg-primary/15 font-medium' : 'hover:bg-primary/5';

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.description?.toLowerCase().includes(query)
    );
  }, [options, search]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value]
  );

  const [previewingValue, setPreviewingValue] = useState('');

  const previewingOption = useMemo(() => {
    if (!multiSelect) return selectedOption;
    if (!previewingValue) return null;
    return options.find((option) => option.value === previewingValue) ?? null;
  }, [multiSelect, options, previewingValue, selectedOption]);

  const handleMultiToggle = (optionValue: string, checked: boolean) => {
    const currentValues = values ?? [];
    const newValues = checked
      ? [...currentValues, optionValue]
      : currentValues.filter((v) => v !== optionValue);
    onMultiChange?.(newValues);
    if (checked) {
      setPreviewingValue(optionValue);
    }
    if (!checked && previewingValue === optionValue) {
      setPreviewingValue(newValues[0] ?? '');
    }
  };

  const dropdownNavigableOptions = useMemo(() => {
    const list: PreviewSelectOption<T>[] = [...filteredOptions];
    if (allowEmpty) {
      list.unshift({ value: '', label: emptyOptionLabel });
    }
    return list;
  }, [allowEmpty, emptyOptionLabel, filteredOptions]);

  const findSelectedOrFirstIndex = () => {
    const selectedIdx = dropdownNavigableOptions.findIndex(
      (opt) => opt.value === value
    );
    if (selectedIdx >= 0) return selectedIdx;
    return dropdownNavigableOptions.length > 0 ? 0 : -1;
  };

  const moveActiveIndex = (direction: 1 | -1) => {
    if (dropdownNavigableOptions.length === 0) {
      setActiveIndex(-1);
      return;
    }

    const start = activeIndex < 0 ? (direction === 1 ? -1 : 0) : activeIndex;
    const next =
      (start + direction + dropdownNavigableOptions.length) %
      dropdownNavigableOptions.length;
    setActiveIndex(next);
    setActiveAction('select');
  };

  const getOptionForActiveIndex = (index: number) => {
    if (index < 0 || index >= dropdownNavigableOptions.length) return null;
    if (allowEmpty && index === 0) return null;
    return dropdownNavigableOptions[index] ?? null;
  };

  const handleOpenPreviewForActiveIndex = () => {
    const option = getOptionForActiveIndex(activeIndex);
    if (!option) return;
    setInfoOption(option);
  };

  const handleCloseInfoModal = () => {
    // Backdrop clicks and close actions should dismiss only the modal,
    // not the underlying dropdown in the same interaction.
    suppressOutsideCloseUntilRef.current = Date.now() + 250;

    const closingValue = infoOption?.value;
    setInfoOption(null);

    if (closingValue) {
      const nextIndex = dropdownNavigableOptions.findIndex(
        (option) => option.value === closingValue
      );
      if (nextIndex >= 0) {
        setActiveIndex(nextIndex);
      }
    }

    setActiveAction('select');

    requestAnimationFrame(() => {
      if (isDropdownOpen) {
        searchInputRef.current?.focus();
        return;
      }
      triggerRef.current?.focus();
    });
  };

  const closeDropdown = (restoreFocus = true) => {
    setIsDropdownOpen(false);
    setSearch('');
    setActiveIndex(-1);
    setActiveAction('select');
    if (restoreFocus) {
      requestAnimationFrame(() => triggerRef.current?.focus());
    }
  };

  const applySelection = (nextValue: string) => {
    onChange?.(nextValue);
    closeDropdown();
  };

  useEffect(() => {
    if (mode !== 'dropdown' || !isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (Date.now() < suppressOutsideCloseUntilRef.current) return;

      // Keep the dropdown open while the details modal is active.
      // Clicking the modal backdrop should close only the modal.
      if (infoOption) return;

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mode, isDropdownOpen, infoOption]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    searchInputRef.current?.focus();
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    setActiveIndex(findSelectedOrFirstIndex());
    setActiveAction('select');
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    if (activeIndex < 0) return;
    if (activeIndex >= dropdownNavigableOptions.length) {
      setActiveIndex(dropdownNavigableOptions.length - 1);
      return;
    }
    const activeOptionId = `${listboxId}-option-${activeIndex}`;
    document.getElementById(activeOptionId)?.scrollIntoView({
      block: 'nearest',
    });
  }, [activeIndex, dropdownNavigableOptions.length, isDropdownOpen, listboxId]);

  useEffect(() => {
    return () => {
      if (typeaheadTimeoutRef.current !== null) {
        window.clearTimeout(typeaheadTimeoutRef.current);
      }
    };
  }, []);

  const handlePrintableTypeahead = (char: string) => {
    if (dropdownNavigableOptions.length === 0) return;

    const nextBuffer = `${typeaheadBufferRef.current}${char.toLowerCase()}`;
    typeaheadBufferRef.current = nextBuffer;

    if (typeaheadTimeoutRef.current !== null) {
      window.clearTimeout(typeaheadTimeoutRef.current);
    }

    typeaheadTimeoutRef.current = window.setTimeout(() => {
      typeaheadBufferRef.current = '';
      typeaheadTimeoutRef.current = null;
    }, 500);

    const start = activeIndex >= 0 ? activeIndex + 1 : 0;
    const ordered = [
      ...dropdownNavigableOptions.slice(start),
      ...dropdownNavigableOptions.slice(0, start),
    ];

    const match = ordered.find((opt) =>
      opt.label.toLowerCase().startsWith(nextBuffer)
    );

    if (!match) return;

    const idx = dropdownNavigableOptions.findIndex((opt) => opt === match);
    if (idx >= 0) {
      setActiveIndex(idx);
    }
  };

  const handleTriggerKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (e.key === 'Escape' && isDropdownOpen) {
      e.preventDefault();
      closeDropdown();
      return;
    }

    if (!isDropdownOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsDropdownOpen(true);
        return;
      }

      if (e.key === 'ArrowUp' || e.key === 'End') {
        e.preventDefault();
        setIsDropdownOpen(true);
        setActiveIndex(Math.max(dropdownNavigableOptions.length - 1, -1));
        return;
      }

      if (e.key === 'Home') {
        e.preventDefault();
        setIsDropdownOpen(true);
        setActiveIndex(dropdownNavigableOptions.length > 0 ? 0 : -1);
        return;
      }

      if (e.key.length === 1 && !e.altKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setIsDropdownOpen(true);
        handlePrintableTypeahead(e.key);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActiveIndex(1);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActiveIndex(-1);
      return;
    }

    if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(dropdownNavigableOptions.length > 0 ? 0 : -1);
      setActiveAction('select');
      return;
    }

    if (e.key === 'End') {
      e.preventDefault();
      setActiveIndex(Math.max(dropdownNavigableOptions.length - 1, -1));
      setActiveAction('select');
      return;
    }

    if (e.key === 'ArrowRight') {
      const activeOption = getOptionForActiveIndex(activeIndex);
      if (activeOption) {
        e.preventDefault();
        setActiveAction('preview');
      }
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setActiveAction('select');
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (activeIndex >= 0 && dropdownNavigableOptions[activeIndex]) {
        if (activeAction === 'preview') {
          handleOpenPreviewForActiveIndex();
        } else {
          applySelection(dropdownNavigableOptions[activeIndex].value);
        }
      }
      return;
    }

    if (e.key === 'Tab') {
      closeDropdown();
      return;
    }

    if (e.key.length === 1 && !e.altKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      handlePrintableTypeahead(e.key);
    }
  };

  const handleSearchKeyDown = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActiveIndex(1);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActiveIndex(-1);
      return;
    }

    if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(dropdownNavigableOptions.length > 0 ? 0 : -1);
      setActiveAction('select');
      return;
    }

    if (e.key === 'End') {
      e.preventDefault();
      setActiveIndex(Math.max(dropdownNavigableOptions.length - 1, -1));
      setActiveAction('select');
      return;
    }

    if (e.key === 'ArrowRight') {
      const activeOption = getOptionForActiveIndex(activeIndex);
      if (activeOption) {
        e.preventDefault();
        setActiveAction('preview');
      }
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setActiveAction('select');
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && dropdownNavigableOptions[activeIndex]) {
        if (activeAction === 'preview') {
          handleOpenPreviewForActiveIndex();
        } else {
          applySelection(dropdownNavigableOptions[activeIndex].value);
        }
      }
      return;
    }

    if (e.key === 'Tab') {
      closeDropdown(false);
    }
  };

  if (mode === 'dropdown') {
    return (
      <FormField
        label={label}
        helperText={helperText}
        helperAction={helperAction}
        spacing={spacing}
        error={error}
        required={required}
        disabled={disabled}
        id={id}
      >
        <div ref={containerRef} className="relative w-full">
          <button
            ref={triggerRef}
            type="button"
            className="border-input bg-background hover:border-ring focus:ring-ring/35 disabled:bg-muted/50 flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm shadow-md transition-all duration-200 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              if (disabled) return;
              setIsDropdownOpen((open) => {
                const next = !open;
                if (!next) {
                  setSearch('');
                  setActiveIndex(-1);
                }
                return next;
              });
            }}
            onKeyDown={handleTriggerKeyDown}
            disabled={disabled}
            role="combobox"
            tabIndex={disabled ? -1 : 0}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            aria-controls={listboxId}
            aria-activedescendant={
              isDropdownOpen && activeIndex >= 0
                ? `${listboxId}-option-${activeIndex}`
                : undefined
            }
          >
            <span
              className={`truncate ${selectedOption ? 'text-foreground' : 'text-muted-foreground'}`}
            >
              {selectedOption?.label ?? triggerPlaceholder}
            </span>
            <ChevronDown
              size={getIconPixels('sm', 'desktop')}
              className={`text-muted-foreground transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isDropdownOpen && (
            <div className="border-border bg-card absolute inset-x-0 z-20 mt-2 w-full max-w-full overflow-hidden rounded-md border shadow-lg">
              <div
                className="border-border border-b p-2"
                onKeyDownCapture={handleSearchKeyDown}
              >
                <Input
                  ref={searchInputRef}
                  type="search"
                  className="h-8 text-sm sm:h-7"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  disabled={disabled}
                  aria-label={searchPlaceholder}
                />
              </div>

              <div
                id={listboxId}
                ref={listboxRef}
                className="max-h-72 overflow-y-auto"
                role="listbox"
              >
                {allowEmpty && (
                  <div className="border-border flex min-w-0 items-center border-b">
                    <button
                      type="button"
                      disabled={disabled}
                      className={`h-auto! min-h-0! w-full min-w-0 rounded-none border-r-2 px-3 py-2 text-left transition-colors ${value === '' ? 'border-r-primary bg-primary/15 font-medium' : 'hover:bg-primary/5 border-r-transparent'} ${activeIndex === 0 ? 'bg-primary/5 ring-border ring-1 ring-inset' : ''}`}
                      id={`${listboxId}-option-0`}
                      role="option"
                      aria-selected={activeIndex === 0}
                      aria-disabled={false}
                      tabIndex={-1}
                      onMouseEnter={() => setActiveIndex(0)}
                      onFocus={() => {
                        setActiveIndex(0);
                        setActiveAction('select');
                      }}
                      onClick={() => applySelection('')}
                    >
                      <div className="min-w-0">
                        <div className="wrap-break-word whitespace-normal">
                          {emptyOptionLabel}
                        </div>
                      </div>
                    </button>
                    <div className="h-6 w-6 shrink-0" aria-hidden="true" />
                  </div>
                )}

                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-4">
                    <Text size="sm" variant="muted">
                      {noneFoundText}
                    </Text>
                  </div>
                ) : (
                  filteredOptions.map((option, idx) => {
                    const isSelected = value === option.value;
                    const optionIndex = allowEmpty ? idx + 1 : idx;
                    return (
                      <div
                        key={option.value}
                        className="border-border flex min-w-0 items-center border-b last:border-b-0"
                      >
                        <button
                          type="button"
                          disabled={disabled}
                          className={`h-auto! min-h-0! w-full min-w-0 rounded-none border-r-2 px-3 py-2 text-left transition-colors ${isSelected ? 'border-r-primary bg-primary/15 font-medium' : 'hover:bg-primary/5 border-r-transparent'} ${activeIndex === optionIndex ? 'bg-primary/5 ring-border ring-1 ring-inset' : ''}`}
                          id={`${listboxId}-option-${optionIndex}`}
                          role="option"
                          aria-selected={activeIndex === optionIndex}
                          aria-disabled={false}
                          tabIndex={-1}
                          onMouseEnter={() => setActiveIndex(optionIndex)}
                          onFocus={() => {
                            setActiveIndex(optionIndex);
                            setActiveAction('select');
                          }}
                          onClick={() => applySelection(option.value)}
                        >
                          <div className="flex min-w-0 items-start gap-2">
                            <Avatar
                              logo={option.logo}
                              alt={option.logoAlt ?? option.label}
                              shape="squircle"
                              className="mt-0.5 shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="wrap-break-word whitespace-normal">
                                {option.label}
                              </div>
                              {option.description && (
                                <div className="text-muted-foreground text-xs wrap-break-word whitespace-normal">
                                  {option.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>

                        <Button
                          variant="ghost-primary"
                          size="icon-xs"
                          disabled={disabled}
                          className={
                            activeIndex === optionIndex &&
                            activeAction === 'preview'
                              ? 'ring-border bg-primary/5 rounded-sm ring-1 ring-inset'
                              : ''
                          }
                          aria-label={`View details for ${option.label}`}
                          onMouseEnter={() => {
                            setActiveIndex(optionIndex);
                            setActiveAction('preview');
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setInfoOption(option);
                          }}
                        >
                          <SearchCheck />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <Dialog
            open={!!infoOption}
            onOpenChange={(o) => !o && handleCloseInfoModal()}
          >
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {infoOption?.label ?? detailsModalTitle}
                </DialogTitle>
                {infoOption?.description && (
                  <DialogDescription>
                    {infoOption.description}
                  </DialogDescription>
                )}
              </DialogHeader>
              {renderPreview(infoOption, { surface: 'modal' })}
              {infoOption ? (
                <DialogFooter>
                  <Button
                    type="button"
                    variant="link"
                    onClick={handleCloseInfoModal}
                    disabled={disabled}
                  >
                    {detailsModalBackLabel}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      onChange?.(infoOption.value);
                      setInfoOption(null);
                      closeDropdown();
                    }}
                    disabled={disabled}
                  >
                    {detailsModalSelectLabel}
                  </Button>
                </DialogFooter>
              ) : null}
            </DialogContent>
          </Dialog>
        </div>
      </FormField>
    );
  }

  return (
    <FormField
      label={label}
      helperText={helperText}
      helperAction={helperAction}
      spacing={spacing}
      error={error}
      required={required}
      disabled={disabled}
      id={id}
    >
      <div className="flex flex-col gap-3 sm:gap-2">
        <div
          className={`grid ${containerHeight} min-w-0 grid-cols-1 items-stretch gap-3 md:grid-cols-[minmax(16rem,1fr)_minmax(0,1fr)]`}
        >
          {/* List — top on mobile, left column on desktop */}
          <div className="order-first flex min-h-0 min-w-0 flex-col gap-2">
            <Input
              type="search"
              className="h-8 text-sm sm:h-7"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              disabled={disabled}
              aria-label={searchPlaceholder}
            />

            <div className="border-border min-h-0 flex-1 overflow-y-auto rounded-md border">
              {allowEmpty && !multiSelect && (
                <button
                  type="button"
                  className={`${rowBaseClass} ${cursorClass} ${getRowStateClass(
                    value === ''
                  )}`}
                  onClick={() => onChange?.('')}
                  disabled={disabled}
                >
                  <div className="flex items-center justify-between gap-2">
                    <Text
                      size="sm"
                      weight={value === '' ? 'medium' : 'normal'}
                      className="wrap-break-word"
                    >
                      {emptyOptionLabel}
                    </Text>
                    {value === '' && (
                      <Check
                        size={getIconPixels('sm', 'desktop')}
                        className="shrink-0"
                      />
                    )}
                  </div>
                </button>
              )}

              {filteredOptions.map((option) => {
                const isSelected = multiSelect
                  ? (values ?? []).includes(option.value)
                  : value === option.value;

                if (multiSelect) {
                  return (
                    <div
                      key={option.value}
                      className={cn(
                        'flex min-w-0 items-center border-b border-border transition-colors last:border-b-0',
                        getRowStateClass(isSelected)
                      )}
                    >
                      <div className="flex items-center px-3 py-2">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            handleMultiToggle(option.value, !!checked)
                          }
                          disabled={disabled}
                          className="mt-0.5 shrink-0"
                        />
                      </div>
                      <button
                        type="button"
                        className={cn(
                          'flex w-full min-w-0 items-center gap-2 py-2 pr-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                          cursorClass
                        )}
                        onClick={() => setPreviewingValue(option.value)}
                        disabled={disabled}
                      >
                        <Avatar
                          logo={option.logo}
                          alt={option.logoAlt ?? option.label}
                          size="md"
                          shape="squircle"
                          className="shrink-0"
                        />
                        <div className="min-w-0">
                          <Text
                            size="base"
                            weight={isSelected ? 'medium' : 'normal'}
                            className="wrap-break-word"
                          >
                            {option.label}
                          </Text>
                          {option.description && (
                            <Text
                              size="xs"
                              variant="muted"
                              className="truncate"
                            >
                              {option.description}
                            </Text>
                          )}
                        </div>
                      </button>
                    </div>
                  );
                }

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`${rowBaseClass} min-w-0 last:border-b-0 ${cursorClass} ${getRowStateClass(
                      isSelected
                    )}`}
                    onClick={() => onChange?.(option.value)}
                    disabled={disabled}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 items-start gap-2">
                        <Avatar
                          logo={option.logo}
                          alt={option.logoAlt ?? option.label}
                          size="md"
                          shape="squircle"
                        />
                        <div className="min-w-0">
                          <Text
                            size="base"
                            weight={isSelected ? 'medium' : 'normal'}
                            className="wrap-break-word"
                          >
                            {option.label}
                          </Text>
                          {option.description && (
                            <Text
                              size="xs"
                              variant="muted"
                              className="truncate"
                            >
                              {option.description}
                            </Text>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <Check
                          size={getIconPixels('sm', 'desktop')}
                          className="shrink-0"
                        />
                      )}
                    </div>
                  </button>
                );
              })}

              {filteredOptions.length === 0 && (
                <div className="px-3 py-4">
                  <Text size="sm" variant="muted">
                    {noneFoundText}
                  </Text>
                </div>
              )}
            </div>
          </div>

          {/* Preview — below list on mobile, right column on desktop */}
          <div className="border-border bg-background order-last min-h-0 min-w-0 overflow-x-hidden overflow-y-auto rounded-md border shadow-sm">
            <div className="min-w-0 p-3 wrap-break-word">
              {renderPreview(multiSelect ? previewingOption : selectedOption, {
                surface: 'pane',
              })}
            </div>
          </div>
        </div>
      </div>
    </FormField>
  );
}

PreviewSelect.displayName = 'PreviewSelect';

export default PreviewSelect;
